/**
 * B480 — the ONE QR renderer (UI law: no app-local encoder). Two faces of
 * one mint path:
 *
 * - `QrCode`: a synchronous SVG built from the `qrcode` package's own
 *   module matrix (TRA-35-ruled dependency), so Tier-2 static renders
 *   carry the real code and the test can compare it bit for bit against
 *   the package's encoding of the same value. Colors are FIXED
 *   black-on-white in both themes — a themed QR is an unscannable one —
 *   and the white field IS the quiet zone (4 modules, the spec's floor).
 *
 * - `ticketQrDataUrl` + `TICKET_QR_OPTIONS`: B68(b)/B448's ticket-QR
 *   mint, moved here from the storefront (which re-exports them) so the
 *   mobile wallets' parity fixtures keep pinning ONE record of how a
 *   ticket QR is made.
 */
import QRCode from "qrcode";

export const TICKET_QR_OPTIONS = {
  errorCorrectionLevel: "M",
  margin: 1,
  scale: 5,
} as const;

export async function ticketQrDataUrl(token: string): Promise<string> {
  return QRCode.toDataURL(token, TICKET_QR_OPTIONS);
}

const QUIET_ZONE = 4;

export function QrCode(props: {
  /** The exact payload — byte-equal with what the scanner must read. */
  readonly value: string;
  /** What this code IS, for a screen reader ("pairing credential QR"). */
  readonly label: string;
  /** Rendered edge in px; the default scans from a phone at arm's length. */
  readonly size?: number;
}): JSX.Element {
  const qr = QRCode.create(props.value, { errorCorrectionLevel: "M" });
  const n = qr.modules.size;
  const total = n + QUIET_ZONE * 2;
  let d = "";
  for (let y = 0; y < n; y += 1) {
    for (let x = 0; x < n; x += 1) {
      if (qr.modules.data[y * n + x] === 1) d += `M${x + QUIET_ZONE} ${y + QUIET_ZONE}h1v1h-1z`;
    }
  }
  const px = props.size ?? 220;
  return (
    <svg
      className="t-qr"
      role="img"
      aria-label={props.label}
      viewBox={`0 0 ${total} ${total}`}
      width={px}
      height={px}
      shapeRendering="crispEdges"
    >
      <rect width={total} height={total} fill="#ffffff" />
      <path d={d} fill="#000000" />
    </svg>
  );
}
